import React from 'react';
import { Dialog, DialogContent, IconButton, Typography, Box, TextField, Button, Stack, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';

const initialForm = {
  fullName: '',
  businessName: '',
  workEmail: '',
  phoneNumber: '',
  message: ''
};

const ContactUsPopup = ({ open, onClose }) => {
  const [formValues, setFormValues] = React.useState(initialForm);
  const [submitted, setSubmitted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setSubmitted(false);
      setFormValues(initialForm);
    }
  }, [open]);

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formValues.fullName || !formValues.workEmail) {
      alert('Please fill in required fields.');
      return;
    }
    setIsSubmitting(true);
    console.log('📤 Submitting contact popup form...', formValues);
    try {
      const payload = {
        name: formValues.fullName,
        email: formValues.workEmail,
        phone_number: formValues.phoneNumber,
        business_name: formValues.businessName,
        complaint_type: 'Contact Inquiry',
        message: formValues.message || 'Contact form submission from landing page'
      };
      console.log('📧 Sending contact form to API...', payload);
      const response = await api.post('/customer/contact-us/create-contact', payload, { noAuth: true, noAutoLogout: true });
      console.log('✅ Contact form submitted successfully!', response);
      console.log('✅ Email sent to support@visibeen.com');
      setSubmitted(true);
      setFormValues(initialForm);
    } catch (error) {
      console.error('❌ Failed to submit contact form:', error);
      alert('Failed to send message. Please try again or email us at support@visibeen.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15,23,42,0.85)',
          backdropFilter: 'blur(4px)'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: 600,
            m: 'auto',
            borderRadius: 3,
            border: '1px solid #1e293b',
            backgroundColor: '#020617',
            boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: 300,
              height: 300,
              background: 'radial-gradient(circle, rgba(14,165,233,0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(100px)',
              pointerEvents: 'none',
              transform: 'translateY(-50%) translateX(50%)'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: 200,
              height: 200,
              background: 'radial-gradient(circle, rgba(34,211,238,0.05) 0%, transparent 70%)',
              borderRadius: '50%',
              filter: 'blur(80px)',
              pointerEvents: 'none',
              transform: 'translateY(33%) translateX(-33%)'
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              px: { xs: 3, sm: 4 },
              pt: { xs: 3.5, sm: 4.5 },
              pb: 3,
              zIndex: 1
            }}
          >
            <IconButton
              onClick={onClose}
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                color: '#94a3b8',
                backgroundColor: 'transparent',
                '&:hover': {
                  color: '#ffffff',
                  backgroundColor: 'rgba(30,41,59,0.5)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            <Box sx={{ mb: 3.5 }}>
              <Typography
                sx={{
                  color: '#0ea5e9',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  mb: 1
                }}
              >
                Contact Us
              </Typography>
              <Typography
                sx={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: '#ffffff',
                  letterSpacing: '-0.5px',
                  mb: 1.5
                }}
              >
                Let's build something great
              </Typography>
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: 16,
                  lineHeight: 1.5,
                  maxWidth: 420
                }}
              >
                Share a few details about your project and our team will get back to you within 24 hours.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: { xs: 3, sm: 4 }, pb: 3, position: 'relative', zIndex: 1 }}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2.5
              }}
            >
              <Box>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  label="Full name"
                  value={formValues.fullName}
                  onChange={handleChange('fullName')}
                  InputLabelProps={{
                    sx: {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: '#0ea5e9'
                      }
                    }
                  }}
                  InputProps={{
                    sx: {
                      backgroundColor: 'rgba(15,23,42,0.5)',
                      color: '#e2e8f0',
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#1e293b',
                        borderWidth: 2
                      },
                      '&:hover fieldset': {
                        borderColor: '#0f172a'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(14,165,233,0.5)',
                        boxShadow: '0 0 0 4px rgba(14,165,233,0.1)'
                      }
                    }
                  }}
                />
              </Box>

              <Box>
                <TextField
                  fullWidth
                  required
                  variant="outlined"
                  label="Business name"
                  value={formValues.businessName}
                  onChange={handleChange('businessName')}
                  InputLabelProps={{
                    sx: {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: '#0ea5e9'
                      }
                    }
                  }}
                  InputProps={{
                    sx: {
                      backgroundColor: 'rgba(15,23,42,0.5)',
                      color: '#e2e8f0',
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#1e293b',
                        borderWidth: 2
                      },
                      '&:hover fieldset': {
                        borderColor: '#0f172a'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(14,165,233,0.5)',
                        boxShadow: '0 0 0 4px rgba(14,165,233,0.1)'
                      }
                    }
                  }}
                />
              </Box>

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    type="email"
                    label="Work email"
                    value={formValues.workEmail}
                    onChange={handleChange('workEmail')}
                    InputLabelProps={{
                      sx: {
                        color: '#64748b',
                        '&.Mui-focused': {
                          color: '#0ea5e9'
                        }
                      }
                    }}
                    InputProps={{
                      sx: {
                        backgroundColor: 'rgba(15,23,42,0.5)',
                        color: '#e2e8f0',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: '#1e293b',
                          borderWidth: 2
                        },
                        '&:hover fieldset': {
                          borderColor: '#0f172a'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(14,165,233,0.5)',
                          boxShadow: '0 0 0 4px rgba(14,165,233,0.1)'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Phone number"
                    value={formValues.phoneNumber}
                    onChange={handleChange('phoneNumber')}
                    InputLabelProps={{
                      sx: {
                        color: '#64748b',
                        '&.Mui-focused': {
                          color: '#0ea5e9'
                        }
                      }
                    }}
                    InputProps={{
                      sx: {
                        backgroundColor: 'rgba(15,23,42,0.5)',
                        color: '#e2e8f0',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: '#1e293b',
                          borderWidth: 2
                        },
                        '&:hover fieldset': {
                          borderColor: '#0f172a'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'rgba(14,165,233,0.5)',
                          boxShadow: '0 0 0 4px rgba(14,165,233,0.1)'
                        }
                      }
                    }}
                  />
                </Grid>
              </Grid>

              <Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  variant="outlined"
                  label="How can we help?"
                  value={formValues.message}
                  onChange={handleChange('message')}
                  InputLabelProps={{
                    sx: {
                      color: '#64748b',
                      '&.Mui-focused': {
                        color: '#0ea5e9'
                      }
                    }
                  }}
                  InputProps={{
                    sx: {
                      backgroundColor: '#ffffff',
                      color: '#0f172a',
                      borderRadius: 2,
                      alignItems: 'flex-start',
                      '& fieldset': {
                        borderColor: '#1e293b',
                        borderWidth: 2
                      },
                      '&:hover fieldset': {
                        borderColor: '#0f172a'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(14,165,233,0.5)',
                        boxShadow: '0 0 0 4px rgba(14,165,233,0.1)'
                      }
                    }
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  mt: 1,
                  height: 48,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: 16,
                  textTransform: 'none',
                  background: isSubmitting ? '#9ca3af' : 'linear-gradient(90deg, #0ea5e9, #22d3ee, #0ea5e9)',
                  backgroundSize: '200% 100%',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundPosition: '100% 0',
                    boxShadow: isSubmitting ? 'none' : '0 0 20px rgba(14,165,233,0.4)'
                  }
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>

              {submitted && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: 2,
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    textAlign: 'center'
                  }}
                >
                  <Typography
                    sx={{
                      color: '#10b981',
                      fontWeight: 600,
                      fontSize: 16,
                      mb: 0.5
                    }}
                  >
                    ✓ Thank You!
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8'
                    }}
                  >
                    Your message has been sent successfully. We'll be in touch shortly.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ px: { xs: 3, sm: 4 }, pb: 3, position: 'relative', zIndex: 1 }}>
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: 13,
                color: '#94a3b8',
                mb: 1.5
              }}
            >
              Or email us directly at:{' '}
              <Box
                component="a"
                href="mailto:support@visibeen.com"
                sx={{ color: '#0ea5e9', textDecoration: 'none', fontWeight: 500, '&:hover': { textDecoration: 'underline' } }}
              >
                support@visibeen.com
              </Box>
            </Typography>
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: 12,
                color: '#64748b'
              }}
            >
              By submitting this form, you agree to our{' '}
              <Box
                component="span"
                sx={{ color: '#94a3b8', cursor: 'pointer', '&:hover': { color: '#0ea5e9' } }}
              >
                Terms
              </Box>{' '}
              and{' '}
              <Box
                component="span"
                sx={{ color: '#94a3b8', cursor: 'pointer', '&:hover': { color: '#0ea5e9' } }}
              >
                Privacy Policy
              </Box>
              .
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ContactUsPopup;
